import {ConditionModel} from "./condition.model";
import {ResourceState, ResourceType} from "../../helpers/Const";
import {ResourceModel as CoreResourceModel} from '../../../core/models/resources/resource.model';

export interface ResourceMeta {
    getAPIVersion(): string;

    getDisplayName(): string;

    getError(): string | undefined;

    getIdentifier(): string;

    getMixedIntoAPIVersion(): string | undefined;

    getMixedIntoType(): string | undefined;

    getName(): string;

    getPath(): string;

    getState(): ResourceState;

    getType(): string;

    isForbidden(): boolean;

    setError(error: string): void;

    setForbidden(state: boolean): void;

}

export class GVTModel {
    group: string;
    version: string;
    type: string;

    constructor(init: Partial<GVTModel>) {
        this.group = init.group;
        this.version = init.version;
        this.type = init.type;
    }

    getAPIVersion(): string {
        return `${this.group}/${this.version}`;
    }

}

export class ReferencedResourceModel implements ResourceMeta {

    relationship: string;
    gvt: GVTModel;
    mixedIntoGVT: GVTModel | null;
    path: string;
    name: string;
    private error: string;
    private forbidden: boolean;

    constructor(init: Partial<ReferencedResourceModel> | never) {
        if (init.gvt instanceof GVTModel) {
            this.gvt = init.gvt;
        } else {
            this.gvt = new GVTModel(init.gvt);
        }
        if (init.mixedIntoGVT instanceof GVTModel) {
            this.mixedIntoGVT = init.mixedIntoGVT;
        } else if (init.mixedIntoGVT) {
            this.mixedIntoGVT = new GVTModel(init.mixedIntoGVT);
        } else {
            this.mixedIntoGVT = null;
        }
        this.relationship = init.relationship;
        this.path = init.path;
        this.name = init.name;
    }

    getIdentifier() {
        return [
            this.gvt.group,
            this.gvt.version,
            this.gvt.type,
            this.path,
            this.name
        ].join("|");
    }

    getName() {
        return this.name;
    }

    getAPIVersion(): string {
        return this.gvt.getAPIVersion();
    }

    getMixedIntoAPIVersion(): string | undefined {
        if (!this.mixedIntoGVT) {
            return undefined;
        }
        return this.mixedIntoGVT.getAPIVersion();
    }

    getMixedIntoType(): string | undefined {
        if (!this.mixedIntoGVT) {
            return undefined;
        }
        return this.mixedIntoGVT.type;
    }

    getDisplayName(): string {
        return this.name;
    }

    getPath(): string {
        return this.path;
    }

    getType(): string {
        return this.gvt.type;
    }

    getState(): ResourceState {
        return ResourceState.UNKNOWN;
    }

    setError(error: string): void {
        this.error = error;
    }

    getError(): string | undefined {
        return this.error;
    }

    isForbidden(): boolean {
        return !!this.forbidden;
    }

    setForbidden(state: boolean) {
        this.forbidden = state;
    }

}

export class MixedIntoModel {
    apiVersion: string;
    type: string;

    constructor(init: Partial<MixedIntoModel> | never) {
        this.apiVersion = init.apiVersion;
        this.type = init.type;
    }
}

export class MetadataModel {
    name: string;
    path: string;
    mixedInto: MixedIntoModel | null;

    constructor(init: Partial<MetadataModel> | never) {
        this.name = init.name;
        this.path = init.path;
        if (init.mixedInto instanceof MixedIntoModel) {
            this.mixedInto = init.mixedInto;
        } else if (init.mixedInto) {
            this.mixedInto = new MixedIntoModel(init.mixedInto);
        } else {
            this.mixedInto = null;
        }
    }
}

export class StatusConditionModel implements ConditionModel {
    constructor(init: Partial<StatusConditionModel> | never) {
        this.lastTransitionTime = init.lastTransitionTime;
        this.message = init.message;
        this.observedGeneration = init.observedGeneration;
        this.reason = init.reason;
        this.type = init.type;
        this.status = init.status;
    }

    lastTransitionTime: string;
    message: string;
    observedGeneration: number;
    reason: ResourceState;
    status: string;
    type: string;
}

class StatusModel {

    conditions: StatusConditionModel[];

    constructor(init: Partial<StatusModel> | never) {
        this.conditions = (init.conditions || []).map(c => new StatusConditionModel(c));
    }
}

export class ResourceDataProvider implements ResourceMeta {

    private data: ResourceModel | ReferencedResourceModel;

    constructor(data: ResourceModel | ReferencedResourceModel) {
        this.data = data;
    }

    setProxyData(data: ResourceModel | ReferencedResourceModel) {
        this.data = data;
    }

    getPath(): string {
        return this.data.getPath();
    }

    getName(): string {
        return this.data.getName();
    }

    getDisplayName(): string {
        return this.data.getDisplayName();
    }

    getAPIVersion(): string {
        return this.data.getAPIVersion();
    }

    getType(): string {
        return this.data.getType();
    }

    getMixedIntoAPIVersion(): string | undefined {
        return this.data.getMixedIntoAPIVersion();
    }

    getMixedIntoType(): string | undefined {
        return this.data.getMixedIntoType();
    }

    getIdentifier(): string {
        return this.data.getIdentifier();
    }

    getState(): ResourceState {
        return this.data.getState();
    }

    getError(): string | undefined {
        return this.data.getError();
    }

    isForbidden(): boolean {
        return this.data.isForbidden();
    }

    setError(error: string): void {
        this.data.setError(error);
    }

    setForbidden(state: boolean) {
        this.data.setForbidden(state);
    }
}

export class ResourceModel implements ResourceMeta {

    apiVersion: string;
    type: ResourceType;
    displayName: string;
    metadata: MetadataModel;
    status: StatusModel;
    state: ResourceState;
    private error: string;
    private forbidden: boolean;

    constructor(init: Partial<ResourceModel> | never) {
        this.apiVersion = init.apiVersion;
        this.type = init.type;
        if (init.metadata instanceof MetadataModel) {
            this.metadata = init.metadata;
        } else {
            this.metadata = new MetadataModel(init.metadata);
        }
        this.status = new StatusModel(init.status ? init.status : {});
        this.state = init.state;
    }

    getIdentifier() {
        return [
            this.apiVersion.split("/")[0], // group
            this.apiVersion.split("/")[1], // version
            this.type,
            this.metadata.path,
            this.metadata.name
        ].join("|");
    }

    getAPIVersion(): string {
        return this.apiVersion;
    }

    getMixedIntoAPIVersion(): string {
        return this.metadata.mixedInto?.apiVersion;
    }

    getMixedIntoType(): string {
        return this.metadata.mixedInto?.type;
    }

    getName(): string {
        return this.metadata.name;
    }

    getDisplayName(): string {
        return this.displayName || this.metadata.name;
    }

    getPath(): string {
        return this.metadata.path;
    }

    getType(): ResourceType {
        return this.type;
    }

    static fromResource(resource: CoreResourceModel) {
        return new ResourceModel({
            ...resource.resource,
            displayName: resource.displayName,
            state: resource.state
        });
    }

    getState(): ResourceState {
        return this.state;
    }

    getError(): string {
        return this.error;
    }

    isForbidden(): boolean {
        return !!this.forbidden;
    }

    setError(error: string): void {
        this.error = error;
    }

    setForbidden(state: boolean) {
        this.forbidden = state;
    }
}

export class ReferencesModel {
    references: ReferencedResourceModel[];
    resource: ResourceModel;

    constructor(init: Partial<ReferencesModel> | never) {
        this.references = (init.references || []).map(r => new ReferencedResourceModel(r));
        if (init.resource instanceof ResourceModel) {
            this.resource = init.resource;
        } else {
            this.resource = new ResourceModel(init.resource);
        }
    }

    getRelationshipsGroupByResource(): Map<string, string[]> {
        const map = new Map<string, string[]>();
        const mapReference = (reference: ReferencedResourceModel) => {
            const key = reference.getIdentifier();
            let refs = map.get(key);
            if (!refs) {
                refs = [];
            }
            refs.push(reference.relationship);
            map.set(key, refs);
        };
        this.references.forEach(mapReference);
        return map;
    }

}
